// src/components/Questionnaires/ObservationTemplatePartD.js

import React, {useEffect, useState} from 'react';
import {
    Card,
    Form,
    Radio,
    Checkbox,
    Input,
    Button,
    Alert,
    Typography,
    message,
    Spin
} from 'antd';
import moment from 'moment';
import "./ObservationTemplatePartD.css";

const {Title, Text} = Typography;
const {TextArea} = Input;

const ObservationTemplatePartD = ({server_url, accessToken}) => {
    const [form] = Form.useForm();
    const [isEligible, setIsEligible] = useState(true);
    const [eligibilityMessage, setEligibilityMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [latestSubmission, setLatestSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch eligibility and latest submission on component mount
    useEffect(() => {
        const checkEligibility = async () => {
            try {
                // Check Eligibility
                const eligibilityResponse = await fetch(`${server_url}/questionnaires/observation-template-partD/eligibility`, {
                    method: 'GET',
                    headers: {Authorization: `Bearer ${accessToken}`},
                });

                if (!eligibilityResponse.ok) {
                    throw new Error('Failed to check eligibility.');
                }

                const eligibilityData = await eligibilityResponse.json();
                setIsEligible(eligibilityData.eligible);
                setEligibilityMessage(eligibilityData.message);

                if (eligibilityData.eligible) {
                    // Fetch the latest submission
                    const latestResponse = await fetch(`${server_url}/questionnaires/observation-template-partD/latest`, {
                        method: 'GET',
                        headers: {Authorization: `Bearer ${accessToken}`},
                    });

                    if (!latestResponse.ok) {
                        throw new Error('Failed to fetch latest submission.');
                    }

                    const latestData = await latestResponse.json();
                    if (latestData.latest_submission) {
                        setLatestSubmission(latestData.latest_submission);
                        // Set form fields directly without mapping
                        form.setFieldsValue(latestData.latest_submission.qna);
                    }
                }
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        checkEligibility();
    }, [server_url, accessToken, form]);

    // Handle form submission
    const handleSubmit = async (values) => {
        // console.log("Form Values:", values); // Original Form Values
        setIsSubmitting(true);
        try {
            // Create a shallow copy of form values to modify
            const transformedValues = {...values};

            // Transform Checkbox.Group fields from array to comma-separated strings if necessary
            const checkboxFields = [
                "What type of classroom interaction was conducted? (Tick all that apply):",
                "What pedagogies were used during the activity? (Tick all that apply):"
            ];

            checkboxFields.forEach(field => {
                if (Array.isArray(transformedValues[field])) {
                    transformedValues[field] = transformedValues[field].join(", ");
                }
            });

            // Debugging Line
            console.log("Transformed Form Values:", transformedValues);

            // Submit the transformed form data to the server
            const response = await fetch(`${server_url}/questionnaires/observation-template-partD/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    form_data: {
                        answers: transformedValues,
                    },
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                // Handle different error structures
                if (errorData.detail) {
                    if (typeof errorData.detail === 'string') {
                        throw new Error(errorData.detail);
                    } else if (Array.isArray(errorData.detail)) {
                        // Convert array of error objects to a readable string
                        const errorMessages = errorData.detail.map(err => {
                            return `${err.loc[err.loc.length - 1]}: ${err.msg}`;
                        }).join("; ");
                        throw new Error(errorMessages);
                    } else if (typeof errorData.detail === 'object') {
                        // Handle other object structures if necessary
                        throw new Error(JSON.stringify(errorData.detail));
                    }
                }
                throw new Error('Failed to submit Observation Template Part D.');
            }

            const data = await response.json();
            message.success('Observation Template Part D submitted successfully!');
            setIsEligible(false);
            setEligibilityMessage(`Next eligible date: ${moment(data.next_eligible_date).format('YYYY-MM-DD')}`);
            // Optionally, you can reset the form or keep the latest submission displayed
        } catch (err) {
            console.error('Error submitting Observation Template Part D:', err);
            message.error(err.message || 'An error occurred while submitting the form.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <Spin tip="Loading..."/>;
    }

    if (error) {
        return <Alert message={error} type="error" showIcon/>;
    }

    return (
        <Card>
            <div className="main-container">
                <div className="obs-block observation-template">
                    <h2 className="ot-header">PART D - Observation Template for Classroom Interactions</h2>

                    <div className="ot-interaction">
                        <h3 className="ot-interaction-title">Tick (✓) the type of classroom interaction.</h3>
                        <label className="ot-label"><input type="checkbox"/> Classroom discussion</label>
                        <label className="ot-label"><input type="checkbox"/> Organised debate</label>
                        <label className="ot-label"><input type="checkbox"/> Simulation/role play</label>
                        <label className="ot-label"><input type="checkbox"/> Lab experiment</label>
                        <label className="ot-label"><input type="checkbox"/> Digital learning</label>
                        <label className="ot-label">Other: <input type="text" className="ot-other-input"/></label>
                    </div>

                    <div className="ot-subject">
                        <h3 className="ot-subject-title">Subject(s)</h3>
                        <textarea className="ot-textarea"></textarea>
                    </div>

                    <div className="ot-curricular">
                        <h3 className="ot-curricular-title">Curricular goal(s)</h3>
                        <textarea className="ot-textarea"></textarea>
                    </div>

                    <div className="ot-competency">
                        <h3 className="ot-competency-title">Competency(-ies)</h3>
                        <textarea className="ot-textarea"></textarea>
                    </div>

                    <div className="ot-pedagogies">
                        <h3 className="ot-pedagogies-title">Pedagogies (Tick ✓ all that apply.)</h3>
                        <label className="ot-label"><input type="checkbox"/> Art-integrated</label>
                        <label className="ot-label"><input type="checkbox"/> Sports-integrated</label>
                        <label className="ot-label"><input type="checkbox"/> Toy-based</label>
                        <label className="ot-label"><input type="checkbox"/> Technology-integrated</label>
                        <label className="ot-label"><input type="checkbox"/> Skill-based learning</label>
                        <label className="ot-label"><input type="checkbox"/> Drama/Theatre-integrated</label>
                        <label className="ot-label"><input type="checkbox"/> Indian Knowledge Systems approaches</label>
                        <label className="ot-label">Any other: <input type="text" className="ot-other-input"/></label>
                    </div>

                    <div className="ot-topic">
                        <h3 className="ot-topic-title">Topic/theme/prompt/question/problem/challenge/planned final
                            output</h3>
                        <textarea className="ot-textarea"></textarea>
                    </div>

                    <div className="ot-materials">
                        <div className="ot-preparation">
                            <h4 className="ot-prep-title">Prior Preparation / Planning / Review / Recap</h4>
                            <textarea className="ot-textarea-80"></textarea>
                        </div>
                        <div className="ot-materials-needed">
                            <h4 className="ot-mat-title">Materials needed (if applicable)</h4>
                            <textarea className="ot-textarea-80"></textarea>
                        </div>
                    </div>
                </div>

                <div className="obs-block learner-reflection">
                    <h2 className="lr-header">Learner Reflection</h2>
                    <p className="lr-instructions"><strong>Tick (✓) all statements that apply.</strong></p>

                    <div className="lr-awareness">
                        <h3 className="lr-title">Awareness</h3>
                        <label className="lr-label"><input type="checkbox"/> I was sufficiently prepared for the
                            activity.</label><br/>
                        <label className="lr-label"><input type="checkbox"/> I demonstrated a solid understanding of the
                            key
                            concepts covered.</label><br/>
                        <label className="lr-label"><input type="checkbox"/> I changed my approach as per the changing
                            dynamics of the activity.</label>
                        <p className="lr-total"><strong>Total number of (✓):</strong> <input type="number"
                                                                                             className="lr-total-input"/>
                        </p>
                    </div>

                    <div className="lr-sensitivity">
                        <h3 className="lr-title">Sensitivity</h3>
                        <label className="lr-label"><input type="checkbox"/> I actively considered different
                            perspectives
                            during the activity.</label><br/>
                        <label className="lr-label"><input type="checkbox"/> I acknowledged the feelings and opinions of
                            my
                            peers.</label><br/>
                        <label className="lr-label"><input type="checkbox"/> I gave constructive feedback in a
                            considerate
                            manner.</label>
                        <p className="lr-total"><strong>Total number of (✓):</strong> <input type="number"
                                                                                             className="lr-total-input"/>
                        </p>
                    </div>

                    <div className="lr-creativity">
                        <h3 className="lr-title">Creativity</h3>
                        <label className="lr-label"><input type="checkbox"/> I approached challenges with a willingness
                            to
                            explore unique solutions.</label><br/>
                        <label className="lr-label"><input type="checkbox"/> I demonstrated flexibility in my
                            thinking.</label><br/>
                        <label className="lr-label"><input type="checkbox"/> I actively sought inspiration from
                            different
                            sources to improve my contribution.</label>
                        <p className="lr-total"><strong>Total number of (✓):</strong> <input type="number"
                                                                                             className="lr-total-input"/>
                        </p>
                    </div>

                    <div className="lr-appreciation">
                        <h3 className="lr-app-title">Leave some words of appreciation/encouragement for yourself:</h3>
                        <textarea className="lr-app-textarea"></textarea>
                    </div>
                </div>

                <div className="obs-block peer-feedback">
                    <h2 className="pf-header">Peer Feedback</h2>
                    <p className="pf-instructions"><strong>Tick (✓) all statements that apply.</strong></p>

                    <div className="pf-awareness">
                        <h3 className="pf-title">Awareness</h3>
                        <label className="pf-label"><input type="checkbox"/> My peer was sufficiently prepared for the
                            activity.</label><br/>
                        <label className="pf-label"><input type="checkbox"/> My peer demonstrated a clear understanding
                            of
                            the key concepts covered.</label><br/>
                        <label className="pf-label"><input type="checkbox"/> My peer changed her/his approach as per the
                            changing dynamics of the activity.</label>
                        <p className="pf-total"><strong>Total number of (✓):</strong> <input type="number"
                                                                                             className="pf-total-input"/>
                        </p>
                    </div>

                    <div className="pf-sensitivity">
                        <h3 className="pf-title">Sensitivity</h3>
                        <label className="pf-label"><input type="checkbox"/> My peer actively considered different
                            perspectives during the activity.</label><br/>
                        <label className="pf-label"><input type="checkbox"/> My peer acknowledged the feelings and
                            opinions
                            of her/his peers.</label><br/>
                        <label className="pf-label"><input type="checkbox"/> My peer gave constructive feedback in a
                            considerate manner.</label>
                        <p className="pf-total"><strong>Total number of (✓):</strong> <input type="number"
                                                                                             className="pf-total-input"/>
                        </p>
                    </div>

                    <div className="pf-creativity">
                        <h3 className="pf-title">Creativity</h3>
                        <label className="pf-label"><input type="checkbox"/> My peer approached challenges with a
                            willingness
                            to explore unique solutions.</label><br/>
                        <label className="pf-label"><input type="checkbox"/> My peer demonstrated flexibility in her/his
                            thinking.</label><br/>
                        <label className="pf-label"><input type="checkbox"/> My peer actively sought inspiration from
                            different sources to improve her/his contribution.</label>
                        <p className="pf-total"><strong>Total number of (✓):</strong> <input type="number"
                                                                                             className="pf-total-input"/>
                        </p>
                    </div>

                    <div className="pf-appreciation">
                        <div className="pf-icon">
                            <img src="https://cdn-icons-png.flaticon.com/512/929/929442.png" alt="User icon"
                                 className="pf-img"/>
                        </div>
                        <div className="pf-text">
                            <h3 className="pf-app-title">Leave some words of appreciation/encouragement for your
                                peer:</h3>
                            <textarea className="pf-app-textarea"></textarea>
                        </div>
                    </div>
                </div>

                <div className="obs-block overview">
                    <h2 className="overview-header">Overview</h2>
                    <p className="overview-description">Tabulate all the feedback for the classroom interaction on this
                        page.</p>

                    <h3 className="overview-teacher-title">Teacher Assessment</h3>
                    <div className="overview-teacher-flex">
                        <textarea className="overview-teacher-textarea"
                                  placeholder="Write some words of appreciation for the learner about their performance."></textarea>
                        <div className="overview-teacher-level">
                            <h4>Tick (✓) the final performance level</h4>
                            <p><strong>Awareness</strong></p>
                            <label><input type="radio" name="awareness_teacher"/> Beginner (0-4)</label><br/>
                            <label><input type="radio" name="awareness_teacher"/> Proficient (5-9)</label><br/>
                            <label><input type="radio" name="awareness_teacher"/> Advanced (10+)</label><br/>
                            <p><strong>Sensitivity</strong></p>
                            <label><input type="radio" name="sensitivity_teacher"/> Beginner (0-4)</label><br/>
                            <label><input type="radio" name="sensitivity_teacher"/> Proficient (5-9)</label><br/>
                            <label><input type="radio" name="sensitivity_teacher"/> Advanced (10+)</label><br/>
                            <p><strong>Creativity</strong></p>
                            <label><input type="radio" name="creativity_teacher"/> Beginner (0-4)</label><br/>
                            <label><input type="radio" name="creativity_teacher"/> Proficient (5-9)</label><br/>
                            <label><input type="radio" name="creativity_teacher"/> Advanced (10+)</label>
                        </div>
                    </div>

                    <h3 className="overview-learner-title">Learner Reflection</h3>
                    <div className="overview-learner-flex">
                        <textarea className="overview-learner-textarea"
                                  placeholder="Write some words of appreciation for yourself."></textarea>
                        <div className="overview-learner-level">
                            <h4>Tick (✓) the final performance level</h4>
                            <p><strong>Awareness</strong></p>
                            <label><input type="radio" name="awareness_learner"/> Beginner (0-4)</label><br/>
                            <label><input type="radio" name="awareness_learner"/> Proficient (5-9)</label><br/>
                            <label><input type="radio" name="awareness_learner"/> Advanced (10+)</label><br/>
                            <p><strong>Sensitivity</strong></p>
                            <label><input type="radio" name="sensitivity_learner"/> Beginner (0-4)</label><br/>
                            <label><input type="radio" name="sensitivity_learner"/> Proficient (5-9)</label><br/>
                            <label><input type="radio" name="sensitivity_learner"/> Advanced (10+)</label><br/>
                            <p><strong>Creativity</strong></p>
                            <label><input type="radio" name="creativity_learner"/> Beginner (0-4)</label><br/>
                            <label><input type="radio" name="creativity_learner"/> Proficient (5-9)</label><br/>
                            <label><input type="radio" name="creativity_learner"/> Advanced (10+)</label>
                        </div>
                    </div>

                    <h3 className="overview-peer-title">Peer Feedback</h3>
                    <div className="overview-peer-flex">
                        <textarea className="overview-peer-textarea"
                                  placeholder="Write some words of appreciation for your peer about their performance."></textarea>
                        <div className="overview-peer-level">
                            <h4>Tick (✓) the final performance level</h4>
                            <p><strong>Awareness</strong></p>
                            <label><input type="radio" name="awareness_peer"/> Beginner (0-4)</label><br/>
                            <label><input type="radio" name="awareness_peer"/> Proficient (5-9)</label><br/>
                            <label><input type="radio" name="awareness_peer"/> Advanced (10+)</label><br/>
                            <p><strong>Sensitivity</strong></p>
                            <label><input type="radio" name="sensitivity_peer"/> Beginner (0-4)</label><br/>
                            <label><input type="radio" name="sensitivity_peer"/> Proficient (5-9)</label><br/>
                            <label><input type="radio" name="sensitivity_peer"/> Advanced (10+)</label><br/>
                            <p><strong>Creativity</strong></p>
                            <label><input type="radio" name="creativity_peer"/> Beginner (0-4)</label><br/>
                            <label><input type="radio" name="creativity_peer"/> Proficient (5-9)</label><br/>
                            <label><input type="radio" name="creativity_peer"/> Advanced (10+)</label>
                        </div>
                    </div>
                </div>

            </div>
            {/* Eligibility Message */}
            {/*<Alert*/}
            {/*    message={eligibilityMessage}*/}
            {/*    type={isEligible ? "success" : "warning"}*/}
            {/*    showIcon*/}
            {/*    style={{marginBottom: 16}}*/}
            {/*/>*/}

            {/* If Eligible, Show the Form */}
            {/*{isEligible ? (*/}
            {/*    <Form*/}
            {/*        layout="vertical"*/}
            {/*        form={form}*/}
            {/*        onFinish={handleSubmit}*/}
            {/*        initialValues={latestSubmission ? latestSubmission.qna : {}}*/}
            {/*    >*/}
            {/*        /!* Classroom Activity Details *!/*/}
            {/*        <Title level={4}>Classroom Activity Details</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What type of classroom interaction was conducted? (Tick all that apply):"*/}
            {/*            name="What type of classroom interaction was conducted? (Tick all that apply):"*/}
            {/*            rules={[{required: true, message: 'Please select at least one interaction type.'}]}*/}
            {/*        >*/}
            {/*            <Checkbox.Group>*/}
            {/*                <Checkbox value="Classroom discussion">Classroom discussion</Checkbox>*/}
            {/*                <Checkbox value="Organized debate">Organized debate</Checkbox>*/}
            {/*                <Checkbox value="Simulation/role play">Simulation/role play</Checkbox>*/}
            {/*                <Checkbox value="Lab experiment">Lab experiment</Checkbox>*/}
            {/*                <Checkbox value="Digital learning">Digital learning</Checkbox>*/}
            {/*                <Checkbox value="Other">Other (Specify: __________)</Checkbox>*/}
            {/*            </Checkbox.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Conditional Field: Other Interaction Type *!/*/}
            {/*        <Form.Item*/}
            {/*            shouldUpdate={(prevValues, currentValues) => prevValues["What type of classroom interaction was conducted? (Tick all that apply):"] !== currentValues["What type of classroom interaction was conducted? (Tick all that apply):"]}>*/}
            {/*            {({getFieldValue}) => {*/}
            {/*                const selected = getFieldValue("What type of classroom interaction was conducted? (Tick all that apply):");*/}
            {/*                return selected && selected.includes("Other") ? (*/}
            {/*                    <Form.Item*/}
            {/*                        label="Please specify the other type of interaction:"*/}
            {/*                        name="Please specify the other type of interaction:"*/}
            {/*                        rules={[{required: true, message: 'Please specify the other interaction type.'}]}*/}
            {/*                    >*/}
            {/*                        <Input placeholder="Specify other interaction type."/>*/}
            {/*                    </Form.Item>*/}
            {/*                ) : null;*/}
            {/*            }}*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What subject(s) were covered during the activity?"*/}
            {/*            name="What subject(s) were covered during the activity?"*/}
            {/*            rules={[{required: true, message: 'Please enter the subjects covered.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., Mathematics, Science, History"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What were the main curricular goals of the activity?"*/}
            {/*            name="What were the main curricular goals of the activity?"*/}
            {/*            rules={[{required: true, message: 'Please describe the curricular goals.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the main curricular goals."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What competencies were targeted during the activity?"*/}
            {/*            name="What competencies were targeted during the activity?"*/}
            {/*            rules={[{required: true, message: 'Please list the competencies.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="e.g., Critical Thinking, Collaboration, Communication"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What pedagogies were used during the activity? (Tick all that apply):"*/}
            {/*            name="What pedagogies were used during the activity? (Tick all that apply):"*/}
            {/*            rules={[{required: true, message: 'Please select at least one pedagogy.'}]}*/}
            {/*        >*/}
            {/*            <Checkbox.Group>*/}
            {/*                <Checkbox value="Art-integrated">Art-integrated</Checkbox>*/}
            {/*                <Checkbox value="Toy-based">Toy-based</Checkbox>*/}
            {/*                <Checkbox value="Technology-integrated">Technology-integrated</Checkbox>*/}
            {/*                <Checkbox value="Drama/Theatre-integrated">Drama/Theatre-integrated</Checkbox>*/}
            {/*                <Checkbox value="Sports-integrated">Sports-integrated</Checkbox>*/}
            {/*                <Checkbox value="Skill-based learning">Skill-based learning</Checkbox>*/}
            {/*                <Checkbox value="Indian Knowledge Systems approaches">Indian Knowledge Systems*/}
            {/*                    approaches</Checkbox>*/}
            {/*                <Checkbox value="Other">Other (Specify: __________)</Checkbox>*/}
            {/*            </Checkbox.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Conditional Field: Other Pedagogy *!/*/}
            {/*        <Form.Item*/}
            {/*            shouldUpdate={(prevValues, currentValues) => prevValues["What pedagogies were used during the activity? (Tick all that apply):"] !== currentValues["What pedagogies were used during the activity? (Tick all that apply):"]}>*/}
            {/*            {({getFieldValue}) => {*/}
            {/*                const selected = getFieldValue("What pedagogies were used during the activity? (Tick all that apply):");*/}
            {/*                return selected && selected.includes("Other") ? (*/}
            {/*                    <Form.Item*/}
            {/*                        label="Please specify the other pedagogy used:"*/}
            {/*                        name="Please specify the other pedagogy used:"*/}
            {/*                        rules={[{required: true, message: 'Please specify the other pedagogy.'}]}*/}
            {/*                    >*/}
            {/*                        <Input placeholder="Specify other pedagogy."/>*/}
            {/*                    </Form.Item>*/}
            {/*                ) : null;*/}
            {/*            }}*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What was the main topic, theme, or question addressed in this activity?"*/}
            {/*            name="What was the main topic, theme, or question addressed in this activity?"*/}
            {/*            rules={[{required: true, message: 'Please describe the main topic or theme.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the main topic, theme, or question."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What was the duration of the activity?"*/}
            {/*            name="What was the duration of the activity?"*/}
            {/*            rules={[{required: true, message: 'Please enter the duration of the activity.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., 30 minutes, 1 hour, etc."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What materials were needed or used for the activity?"*/}
            {/*            name="What materials were needed or used for the activity?"*/}
            {/*            rules={[{required: true, message: 'Please list the materials used.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="List the materials used."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Teacher Assessment *!/*/}
            {/*        <Title level={4}>Teacher Assessment</Title>*/}

            {/*        /!* Awareness *!/*/}
            {/*        <Title level={5}>Awareness</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Did the learner demonstrate a clear understanding of the key concepts?"*/}
            {/*            name="Did the learner demonstrate a clear understanding of the key concepts?"*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Was the learner prepared for the activity?"*/}
            {/*            name="Was the learner prepared for the activity?"*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Did the learner connect the topic with real-life applications?"*/}
            {/*            name="Did the learner connect the topic with real-life applications?"*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Were the learner’s questions relevant to the activity?"*/}
            {/*            name="Were the learner’s questions relevant to the activity?"*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Sensitivity *!/*/}
            {/*        <Title level={5}>Sensitivity</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Did the learner acknowledge diverse perspectives?"*/}
            {/*            name="Did the learner acknowledge diverse perspectives?"*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Did the learner engage with peers respectfully?"*/}
            {/*            name="Did the learner engage with peers respectfully?"*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Was there a focus on inclusivity in discussions?"*/}
            {/*            name="Was there a focus on inclusivity in discussions?"*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Did the learner understand emotional implications during the activity?"*/}
            {/*            name="Did the learner understand emotional implications during the activity?"*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Creativity *!/*/}
            {/*        <Title level={5}>Creativity</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Did the learner suggest novel solutions?"*/}
            {/*            name="Did the learner suggest novel solutions?"*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Did the learner demonstrate flexibility in thinking?"*/}
            {/*            name="Did the learner demonstrate flexibility in thinking?"*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Were alternative perspectives considered by the learner?"*/}
            {/*            name="Were alternative perspectives considered by the learner?"*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Did the learner actively engage in problem-solving?"*/}
            {/*            name="Did the learner actively engage in problem-solving?"*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Learner Reflection *!/*/}
            {/*        <Title level={4}>Learner Reflection</Title>*/}

            {/*        /!* Awareness *!/*/}
            {/*        <Title level={5}>Awareness</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="I was sufficiently prepared for the activity."*/}
            {/*            name="I was sufficiently prepared for the activity."*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="I demonstrated a solid understanding of the key concepts covered."*/}
            {/*            name="I demonstrated a solid understanding of the key concepts covered."*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="I adapted my approach as per the changing dynamics of the activity."*/}
            {/*            name="I adapted my approach as per the changing dynamics of the activity."*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Sensitivity *!/*/}
            {/*        <Title level={5}>Sensitivity</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="I considered different perspectives during the activity."*/}
            {/*            name="I considered different perspectives during the activity."*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="I acknowledged the feelings and opinions of my peers."*/}
            {/*            name="I acknowledged the feelings and opinions of my peers."*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="I provided constructive feedback respectfully."*/}
            {/*            name="I provided constructive feedback respectfully."*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Creativity *!/*/}
            {/*        <Title level={5}>Creativity</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="I approached challenges with a willingness to explore unique solutions."*/}
            {/*            name="I approached challenges with a willingness to explore unique solutions."*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="I demonstrated flexibility in my thinking."*/}
            {/*            name="I demonstrated flexibility in my thinking."*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="I sought inspiration from various sources to improve my participation."*/}
            {/*            name="I sought inspiration from various sources to improve my participation."*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Peer Feedback *!/*/}
            {/*        <Title level={4}>Peer Feedback</Title>*/}

            {/*        /!* Awareness *!/*/}
            {/*        <Title level={5}>Awareness</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="My peer was sufficiently prepared for the activity."*/}
            {/*            name="My peer was sufficiently prepared for the activity."*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="My peer demonstrated a clear understanding of the key concepts covered."*/}
            {/*            name="My peer demonstrated a clear understanding of the key concepts covered."*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="My peer adapted to changing situations effectively."*/}
            {/*            name="My peer adapted to changing situations effectively."*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Sensitivity *!/*/}
            {/*        <Title level={5}>Sensitivity</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="My peer acknowledged diverse opinions during the activity."*/}
            {/*            name="My peer acknowledged diverse opinions during the activity."*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="My peer provided feedback in a constructive manner."*/}
            {/*            name="My peer provided feedback in a constructive manner."*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="My peer engaged respectfully in group discussions."*/}
            {/*            name="My peer engaged respectfully in group discussions."*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Creativity *!/*/}
            {/*        <Title level={5}>Creativity</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="My peer employed unique methods for data collection or presentation."*/}
            {/*            name="My peer employed unique methods for data collection or presentation."*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Were the findings engaging and impactful?"*/}
            {/*            name="Were the findings engaging and impactful?"*/}
            {/*            rules={[{required: true, message: 'Please select Yes or No.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Write a few words of appreciation for your peer:"*/}
            {/*            name="Write a few words of appreciation for your peer:"*/}
            {/*            rules={[{required: true, message: 'Please write your appreciation.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Write your appreciation for your peer."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Performance Summary *!/*/}
            {/*        <Title level={4}>Performance Summary</Title>*/}

            {/*        /!* Awareness *!/*/}
            {/*        <Form.Item*/}
            {/*            label="Awareness:"*/}
            {/*            name="awareness_rating" // Unique name*/}
            {/*            rules={[{required: true, message: 'Please provide a rating for Awareness.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Beginner (0-1)">Beginner (0-1)</Radio>*/}
            {/*                <Radio value="Proficient (2-3)">Proficient (2-3)</Radio>*/}
            {/*                <Radio value="Advanced (4-5)">Advanced (4-5)</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Sensitivity *!/*/}
            {/*        <Form.Item*/}
            {/*            label="Sensitivity:"*/}
            {/*            name="sensitivity_rating" // Unique name*/}
            {/*            rules={[{required: true, message: 'Please provide a rating for Sensitivity.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Beginner (0-1)">Beginner (0-1)</Radio>*/}
            {/*                <Radio value="Proficient (2-3)">Proficient (2-3)</Radio>*/}
            {/*                <Radio value="Advanced (4-5)">Advanced (4-5)</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Creativity *!/*/}
            {/*        <Form.Item*/}
            {/*            label="Creativity:"*/}
            {/*            name="creativity_rating" // Unique name*/}
            {/*            rules={[{required: true, message: 'Please provide a rating for Creativity.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Beginner (0-1)">Beginner (0-1)</Radio>*/}
            {/*                <Radio value="Proficient (2-3)">Proficient (2-3)</Radio>*/}
            {/*                <Radio value="Advanced (4-5)">Advanced (4-5)</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Comments on strengths and areas for improvement:"*/}
            {/*            name="Comments on strengths and areas for improvement:"*/}
            {/*            rules={[{*/}
            {/*                required: true,*/}
            {/*                message: 'Please provide comments on strengths and areas for improvement.'*/}
            {/*            }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Provide comments on strengths and areas for improvement."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Self-reflection summary by the learner:"*/}
            {/*            name="Self-reflection summary by the learner:"*/}
            {/*            rules={[{required: true, message: 'Please provide your self-reflection summary.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Provide your self-reflection summary."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Peer evaluation summary:"*/}
            {/*            name="Peer evaluation summary:"*/}
            {/*            rules={[{required: true, message: 'Please provide your peer evaluation summary.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Provide your peer evaluation summary."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Submit Button *!/*/}
            {/*        <Form.Item>*/}
            {/*            <Button type="primary" className="bg-yellow-500 hover:bg-yellow-600 text-black" htmlType="submit" loading={isSubmitting}>*/}
            {/*                Submit*/}
            {/*            </Button>*/}
            {/*        </Form.Item>*/}
            {/*    </Form>*/}
            {/*) : (*/}
            {/*    <Text type="warning">*/}
            {/*        You can only submit this form once every 15 days. Please come back later.*/}
            {/*    </Text>*/}
            {/*)}*/}

            {/* Display Latest Submission */}
            {latestSubmission && isEligible && (
                <Card title="Your Latest Submission" style={{marginTop: 24}}>
                    {Object.entries(latestSubmission.qna).map(([question, answer], index) => (
                        <div key={index} style={{marginBottom: 16}}>
                            <Text strong>{question}</Text>
                            <br/>
                            <Text>{answer}</Text>
                        </div>
                    ))}
                </Card>
            )}
        </Card>
    );

};

export default ObservationTemplatePartD;
